/**
 * Selling a consigned work, decided: whether each of the five steps may be
 * left, what the consignment and the summary read back, and the request that
 * records the sale.
 *
 * Pure. A draft and the consignment go in; gates, rows and a wire body come
 * out. The copy is `sellWizard.constants.ts`; the per-consignment drafts, the
 * buyer check and the write are `useSellWizardController.ts`; the inventory
 * that opens the wizard is `useInventoryController.ts`. What the server
 * decides — splits summing to the whole, whether a buyer id resolves — is not
 * restated here; it arrives as an error and is rendered.
 *
 * Two boundaries: percentages live as whole percent and become basis points
 * only in `toEffectSaleRequest`, and money lives as typed text and becomes
 * minor units only through `parseMoneyMinor`, which refuses a third decimal
 * rather than rounding a price nobody agreed to. An ONDO buyer is gated on
 * the id the director actually ticked, never on a boolean that could outlive
 * a retyped field.
 */

import type {
  ConsignedWork,
  EffectSaleRequest,
  TransferChannel,
  VatScheme,
} from "@ondo/shared";
import { formatMoney } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { namedBuyer } from "../lib/buyer.ts";
import type { BuyerMode } from "../lib/buyer.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import { SELL_COPY } from "./sellWizard.constants.ts";
import { describeIdentity, formatConsignedDate } from "./gallery.model.ts";

/**
 * Selling a consigned work: the five steps, and the arithmetic behind them.
 *
 * A STAGE UNION, NOT FIVE URLS. `useCollectorController` already made this
 * argument for the register flow and it holds harder here: a wizard step is not
 * an address, because arriving at step 4 by typing is arriving with no buyer, no
 * price and no consignment — and the one thing a URL is for is being arrived at
 * out of order.
 *
 * WHAT THE SERVER DECIDES, THIS FILE DOES NOT RESTATE. The splits summing to
 * 10000 is a check constraint (`covenants_splits_are_the_whole_share`); whether
 * the buyer's id exists, is the caller, or is already the holder is one opaque
 * refusal inside `ondo.effect_sale()`. Both arrive as errors and are rendered.
 * What is here is only what the FORM must know to be usable: whether a step can
 * be left, and how to turn typing into the minor units the wire carries.
 */
export type SellStep = "consignment" | "buyer" | "covenant" | "fee" | "convert";

/** In order. The stepper renders this, and `advance` walks it. */
export const SELL_STEPS: readonly SellStep[] = [
  "consignment",
  "buyer",
  "covenant",
  "fee",
  "convert",
];

/**
 * Which field names the buyer — never both, and never a search.
 *
 * The design calls these "Match on network" and "Create new owner". Neither
 * looks anybody up: `ondo` submits an ONDO id the gallery WAS GIVEN, and `name`
 * submits the seller's own typing against no identity at all. An ONDO id is
 * attached, never discovered, and `ondo.effect_sale()` resolves it inside a
 * definer that answers with two verdicts so the id space stays unwalkable.
 *
 * Lifted to `lib/buyer.ts` when OND-173's owner's sale became its second
 * caller — the register `parseMoneyMinor` moved in, below. Re-exported so this
 * module's callers and tests keep their import.
 */
export type { BuyerMode };

/**
 * The form as it accumulates.
 *
 * Percentages live here as WHOLE PERCENT because a slider yields integers and a
 * reader thinks in them; they become basis points in `toEffectSaleRequest` and
 * are never a float in between. The price and the VAT live here as TYPED TEXT,
 * because "1,450,000" is what somebody writes and `priceMinor` is what the wire
 * takes — parsing at the boundary is the whole reason the server never sees a
 * format to guess at.
 */
export interface SellDraft {
  buyerMode: BuyerMode;
  buyerName: string;
  buyerOndoId: string;
  /** As typed. `parseMoneyMinor` is the only thing that reads it. */
  price: string;
  currency: string;
  channel: TransferChannel;
  addVat: boolean;
  vatScheme: VatScheme;
  vat: string;
  covenantOn: boolean;
  termYears: number;
  /** Whole percent of resale PROFIT, not of the resale price. */
  resaleSharePct: number;
  /** Whole percent to the gallery. The artist takes the remainder. */
  gallerySharePct: number;
  artistElected: boolean;
  locksTransfer: boolean;
  /** Step 5's one gate. Recorded as a dated, attributable act by the row itself. */
  confirmedTitle: boolean;
}

/**
 * The draft a work opens with.
 *
 * Prefilled from the CONSIGNMENT rather than from nothing: the asking price and
 * its currency are terms both sides already agreed, so a blank field would make
 * a director retype a number the registry is holding. Everything about the buyer
 * starts empty, because there is nothing on the row that could honestly guess it.
 *
 * Takes a NULLABLE work because the dialog stays mounted when shut — closing by
 * unmounting would skip the `close()` that returns focus to the button which
 * opened it — so the view asks for a draft on renders where there is no work.
 */
export function initialDraft(work: ConsignedWork | null): SellDraft {
  return {
    buyerMode: "ondo",
    buyerName: "",
    buyerOndoId: "",
    price: !work || work.askingMinor === null ? "" : String(work.askingMinor / 100),
    currency: work?.currency ?? SELL_COPY.defaultCurrency,
    channel: "gallery",
    addVat: false,
    vatScheme: "none",
    vat: "",
    covenantOn: false,
    termYears: SELL_COPY.covenantDefaults.termYears,
    resaleSharePct: SELL_COPY.covenantDefaults.resaleSharePct,
    gallerySharePct: SELL_COPY.covenantDefaults.gallerySharePct,
    artistElected: true,
    locksTransfer: true,
    confirmedTitle: false,
  };
}

/**
 * Typed money into minor units, or null when it is not money.
 *
 * Mirrors `formatMoney`'s hundredth, which is the assumption this domain already
 * makes everywhere it prints a price. Grouping separators and a leading symbol
 * are stripped because they are what a person types; a THIRD decimal place is
 * refused rather than rounded, because rounding somebody's price silently is how
 * a sale gets recorded at a number nobody agreed to.
 *
 * Returns null for zero and for negatives too: the server's own validator is
 * `int().positive()`, and a free work is not a sale this door can record.
 */
// Lifted to `lib/money.ts` when the estate's forms became its second caller;
// re-exported so this module's callers and tests keep their import.
export { parseMoneyMinor };

/** The artist's half of the split. One number, so the two cannot disagree. */
export function artistSharePct(draft: SellDraft): number {
  return 100 - draft.gallerySharePct;
}

/**
 * Whether this step may be left.
 *
 * Only the buyer step has a real gate, and it is the CLIENT half of
 * `effectSaleSchema`'s refine — exactly one kind of buyer, and a price that
 * parses. Step 5 gates on the title confirmation, which is not a validation at
 * all but a statement the director is making. The other three collect nothing
 * that can be wrong: step 1 is a reading, step 3 has defaults, step 4 sends
 * nothing.
 *
 * AN ONDO BUYER MUST ALSO BE CONFIRMED, and this is a GATE where the agent
 * dialog's identical check is only a courtesy. The difference is what the two
 * acts cost when the id is wrong: a mandate is ended with one call and nothing
 * moved, while a sale offers a stranger title to somebody else's picture and can
 * only be unsaid by a withdrawal that stays on the record. So the name has to be
 * read and agreed to, not merely offered.
 *
 * `confirmedOndoId` is the id the director actually ticked, rather than a
 * boolean. A boolean would stay true under a retyped id, which is the one state
 * this step must not be able to reach — see `namedBuyer`'s docblock for the
 * same argument about the mode toggle.
 */
export function canContinue(
  step: SellStep,
  draft: SellDraft,
  confirmedOndoId: string | null = null,
): boolean {
  if (step === "buyer") {
    if (parseMoneyMinor(draft.price) === null) return false;
    if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) return false;
    if (draft.addVat && draft.vat !== "" && parseMoneyMinor(draft.vat) === null) {
      return false;
    }

    const buyer = namedBuyer(draft);
    if (buyer === null) return false;

    // An off-registry buyer has nobody to confirm. There is no row, no name to
    // read back and nothing the registry could check — which is the whole of
    // `unregistered`, and why the two halves of this toggle are gated
    // differently rather than uniformly.
    return buyer.kind === "name" || buyer.ondoId === confirmedOndoId;
  }

  if (step === "convert") return draft.confirmedTitle;

  return true;
}

/**
 * The buyer, one way or the other, or null when neither or both is filled.
 *
 * `buyerMode` decides which field is READ, so a name left behind by switching
 * the toggle cannot be submitted alongside an id. The XOR the server refuses is
 * therefore unreachable from this form — which is why this returns a discriminated
 * value rather than a boolean: the submit builds from the same answer the gate
 * used, and the two cannot drift.
 *
 * Lifted to `lib/buyer.ts` alongside `BuyerMode` when OND-173's owner's sale
 * became its second caller; re-exported so this module's callers and tests keep
 * their import. `SellDraft` satisfies `BuyerFields` structurally, so nothing at
 * a call site changed.
 */
export { namedBuyer };

/**
 * The registration fee, WHICH THIS SCREEN DOES NOT CHARGE.
 *
 * The step names the tier that applies and writes nothing: the row in
 * `ondo.registration_fees` is written by a trigger on `ondo.transfers` when
 * the sale is effected (20260910110000, decision 2), and "recorded as paid"
 * is the Transaction Centre's to assert, never this wizard's. There is still
 * no payments provider, so the copy says the fee is invoiced separately
 * rather than implying money moved.
 */
export function feeFor(draft: SellDraft): { kind: "plain" | "premium"; amount: string } {
  return draft.covenantOn
    ? { kind: "premium", amount: SELL_COPY.fee.premium }
    : { kind: "plain", amount: SELL_COPY.fee.plain };
}

/** The price as the summary and the header print it. '' when it does not parse. */
export function describeDraftPrice(draft: SellDraft): string {
  const minor = parseMoneyMinor(draft.price);
  if (minor === null) return "";

  return formatMoney({ minor, currency: draft.currency.trim().toUpperCase() });
}

/**
 * The draft as the wire takes it.
 *
 * Throws rather than returning a partial request: it is called behind
 * `canContinue("buyer", …)` and `canContinue("convert", …)`, and a submit that
 * silently dropped a buyer would be the one bug in this file worth fearing.
 *
 * `soldAt` is not sent. The server defaults it to now, which is what a sale
 * recorded as it happens means — back-dating is a real need and it is a field
 * this form does not yet offer rather than one it guesses at.
 */
export function toEffectSaleRequest(draft: SellDraft): EffectSaleRequest {
  const priceMinor = parseMoneyMinor(draft.price);
  const buyer = namedBuyer(draft);
  if (priceMinor === null || buyer === null) {
    throw new Error("The sale is not complete enough to send.");
  }

  const vatMinor = draft.addVat ? parseMoneyMinor(draft.vat) : null;

  return {
    priceMinor,
    currency: draft.currency.trim().toUpperCase(),
    ...(buyer.kind === "ondo" ? { buyerOndoId: buyer.ondoId } : { buyerName: buyer.name }),
    channel: draft.channel,
    vatScheme: draft.addVat ? draft.vatScheme : "none",
    vatMinor,
    covenant: draft.covenantOn
      ? {
          termYears: draft.termYears,
          // BASIS POINTS, and the conversion happens exactly here. 50% is 5000;
          // a percentage stored as a float is a rounding argument waiting to
          // happen, which is the reason the columns are bp in the first place.
          resaleShareBp: draft.resaleSharePct * 100,
          gallerySplitBp: draft.gallerySharePct * 100,
          artistSplitBp: artistSharePct(draft) * 100,
          artistElected: draft.artistElected,
          locksTransfer: draft.locksTransfer,
        }
      : null,
  };
}

/** One fact about the consignment, as step 1 lists them. */
export interface ConsignmentFact {
  label: string;
  value: string;
}

/**
 * The consignment, read back before anything is decided.
 *
 * THE DESIGN'S "Consignment agreement — ✓ ON FILE" CARD IS NOT HERE, and its
 * absence is the honest half of this step. No document exists in the schema, and
 * a card asserting one was on file would be a screen making a claim the registry
 * cannot stand behind — the failure `works.verification` was dropped to avoid,
 * in a place where somebody is about to move title. What IS shown is every term
 * the row actually holds, which is what the agreement would have said anyway.
 */
export function consignmentFacts(work: ConsignedWork): ConsignmentFact[] {
  const facts: ConsignmentFact[] = [
    { label: SELL_COPY.facts.consignor, value: work.consignorName },
    { label: SELL_COPY.facts.consignorId, value: work.consignorOndoId },
    {
      label: SELL_COPY.facts.arrangement,
      value: SELL_COPY.arrangement[work.arrangement],
    },
  ];

  if (work.arrangement === "consignment") {
    facts.push({
      label: SELL_COPY.facts.consignedOn,
      value: formatConsignedDate(work.offeredAt),
    });
  }

  const basis = SELL_COPY.basis[work.basis];
  if (basis) facts.push({ label: SELL_COPY.facts.basis, value: basis });

  if (work.askingMinor !== null && work.currency) {
    facts.push({
      label: SELL_COPY.facts.asking,
      value: formatMoney({ minor: work.askingMinor, currency: work.currency }),
    });
  }

  // The floor is the number this gallery MAY NOT GO BELOW, so it is shown on the
  // step where a price is about to be typed rather than left on the row. The
  // form does not enforce it — `ondo.consignments` does not either, and a client
  // refusing what the database accepts would be an application-level rule
  // pretending to be a constraint.
  if (work.floorMinor !== null && work.currency) {
    facts.push({
      label: SELL_COPY.facts.floor,
      value: formatMoney({ minor: work.floorMinor, currency: work.currency }),
    });
  }

  if (work.note) facts.push({ label: SELL_COPY.facts.note, value: work.note });

  return facts;
}

/** The last screen's read-back, built from the same values the submit sends. */
export function summaryRows(work: ConsignedWork, draft: SellDraft): ConsignmentFact[] {
  const buyer = namedBuyer(draft);

  const rows: ConsignmentFact[] = [
    { label: SELL_COPY.summary.work, value: `${work.title} · ${describeIdentity(work)}` },
    {
      label: SELL_COPY.summary.buyer,
      value: buyer === null ? "" : buyer.kind === "ondo" ? buyer.ondoId : buyer.name,
    },
    { label: SELL_COPY.summary.price, value: describeDraftPrice(draft) },
    { label: SELL_COPY.summary.channel, value: SELL_COPY.channel[draft.channel] },
  ];

  if (draft.addVat) {
    rows.push({
      label: SELL_COPY.summary.vat,
      value: SELL_COPY.vatScheme[draft.vatScheme],
    });
  }

  rows.push({
    label: SELL_COPY.summary.code,
    value: draft.covenantOn && draft.locksTransfer
      ? SELL_COPY.summary.codeLocked
      : SELL_COPY.summary.codeRed,
  });

  rows.push({
    label: SELL_COPY.summary.covenant,
    value: draft.covenantOn
      ? SELL_COPY.summary.covenantTerms(
          draft.termYears,
          draft.resaleSharePct,
          draft.gallerySharePct,
          artistSharePct(draft),
        )
      : SELL_COPY.summary.noCovenant,
  });

  rows.push({ label: SELL_COPY.summary.fee, value: feeFor(draft).amount });

  return rows;
}

/**
 * What went wrong, in words a director can act on.
 *
 * PREFERS THE SERVER'S OWN SENTENCE for every refusal it makes, because
 * `ondo.effect_sale()` raises three different 42501s and only it knows which:
 * no consignment you can sell under, a buyer id it will not resolve, and — the
 * one this form cannot predict — a work locked by a covenant from an EARLIER
 * sale. `ConsignedWork` carries no lock state, so a red resale row can look
 * sellable right up to the moment it is refused, and replacing that sentence
 * with a house one would tell the director the wrong thing about their own stock.
 */
export function describeSaleError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 429) return SELL_COPY.errors.throttled;
    if (cause.status === 401) return SELL_COPY.errors.signedOut;
    if (cause.message) return cause.message;

    return SELL_COPY.errors.refused;
  }

  return cause instanceof Error ? cause.message : SELL_COPY.errors.refused;
}

/**
 * The step Continue goes to. Stays put on the last one — the button that
 * would press it is not offered there, so this is what a stray call does.
 */
export function nextStep(at: SellStep): SellStep {
  return SELL_STEPS[Math.min(SELL_STEPS.indexOf(at) + 1, SELL_STEPS.length - 1)]!;
}

/** The step Back goes to. Stays put on the first one, for the same reason. */
export function previousStep(at: SellStep): SellStep {
  return SELL_STEPS[Math.max(SELL_STEPS.indexOf(at) - 1, 0)]!;
}
